import type { ParallaxOffsetTypeSettings, ParallaxSpeed } from '#types/components/cms/parallax'
import type { ParallaxElementConfig } from 'parallax-controller'

const offsetMap: Record<string, string> = {
  '-5': '-250px',
  '-4': '-200px',
  '-3': '-150px',
  '-2': '-100px',
  '-1': '-50px',
  '0': '0px',
  '1': '50px',
  '2': '100px',
  '3': '150px',
  '4': '200px',
  '5': '250px'
}

const percentToOpacity = (value: string): number => {
  if (!value) return 1
  const num = Number.parseFloat(value.replace('%', ''))
  return Math.max(0, Math.min(1, num / 100))
}

type OffsetLayerParallaxOptions = {
  /**
   * Ends the animation no later than the moment the element's bottom edge crosses the vertical
   * center of the viewport. Without it a `slow` layer keeps animating until the element has
   * scrolled off the top of the screen.
   */
  finishAtCenter?: boolean
}

/**
 * Builds a `parallax-controller` element config from CMS offset-type layer settings
 * (used for both the media and the text layer of the offset parallax effect).
 * Returns `undefined` when the layer is not configured (parallax disabled).
 * The speed-based `rootMargin` depends on the viewport, so the returned config is
 * only meaningful on the client; on the server it degrades to a zero root margin.
 * @param layer - CMS layer settings (`startOffset`/`endOffset` steps, opacity percents, speed, easing).
 * @param options - Behaviour overrides for the generated config.
 * @param options.finishAtCenter - Caps how late the animation is allowed to complete, ending it no
 * later than the moment the element's bottom edge crosses the vertical center of the viewport.
 * @returns Parallax element config, or `undefined` when the layer is disabled.
 * @category Utils
 */
export const getOffsetLayerParallaxConfig = (
  layer?: ParallaxOffsetTypeSettings,
  { finishAtCenter }: OffsetLayerParallaxOptions = {}
): ParallaxElementConfig | undefined => {
  if (!layer) return undefined

  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight
  const speedMap: Record<ParallaxSpeed, number> = {
    slow: 0,
    medium: -viewportHeight / 1.75,
    fast: -viewportHeight / 1.25,
  }

  // `rootMargin.bottom` moves the element's bottom edge, which the controller uses as the end of
  // the scroll window: at progress 1 that edge sits `-bottom` px below the top of the viewport.
  // `slow` leaves it at 0, so the animation only completes once the element clears the screen top.
  const bottom = finishAtCenter
    ? Math.min(speedMap[layer.speed], -viewportHeight / 2)
    : speedMap[layer.speed]

  return {
    ...(layer.startOffset && {
      translateY: [offsetMap[layer.startOffset], offsetMap[layer.endOffset] || '0px']
    }),
    ...(layer.startOpacity && {
      opacity: [percentToOpacity(layer.startOpacity), percentToOpacity(layer.endOpacity)]
    }),
    easing: layer.easing,
    shouldAlwaysCompleteAnimation: true,
    rootMargin: {
      top: 0,
      bottom,
      left: 0,
      right: 0,
    },
  }
}
